import React, { useRef, useEffect, useState } from 'react';
import './MusicPlayer.css';

const MUSIC_OWNER_KEY = 'globalMusicOwnerTabId';
const MUSIC_TIME_KEY = 'globalMusicCurrentTime';
const MUSIC_USER_PREFERENCE = 'globalMusicUserPreference'; // 'play' or 'pause'
const MUSIC_BROWSER_SESSION_COOKIE = 'musicBrowserSession=1';
const MUSIC_FORCE_PAUSE_EVENT = 'global-music-force-pause';

const MusicPlayer: React.FC = () => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const tabIdRef = useRef<string>(`tab-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const wasPlayingBeforeHidden = useRef<boolean>(false);
    const titleViewportRef = useRef<HTMLDivElement>(null);
    const titleTextRef = useRef<HTMLSpanElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [isTitleOverflowing, setIsTitleOverflowing] = useState(false);

    const isCurrentTabOwner = () => localStorage.getItem(MUSIC_OWNER_KEY) === tabIdRef.current;

    const claimOwner = () => {
        const currentOwner = localStorage.getItem(MUSIC_OWNER_KEY);
        if (!currentOwner || currentOwner === tabIdRef.current) {
            localStorage.setItem(MUSIC_OWNER_KEY, tabIdRef.current);
            return true;
        }
        return false;
    };

    const savePlaybackSnapshot = (time: number) => {
        localStorage.setItem(MUSIC_TIME_KEY, time.toString());
    };

    const saveUserPreference = (preference: 'play' | 'pause') => {
        localStorage.setItem(MUSIC_USER_PREFERENCE, preference);
    };

    const getUserPreference = (): 'play' | 'pause' | null => {
        const value = localStorage.getItem(MUSIC_USER_PREFERENCE);
        return value as 'play' | 'pause' | null;
    };

    const releaseOwner = () => {
        if (!isCurrentTabOwner()) return;

        // 오디오 ref가 살아있을 때만 시간 저장하고, 소유권 키는 항상 해제한다.
        if (audioRef.current) {
            savePlaybackSnapshot(audioRef.current.currentTime);
        }
        localStorage.removeItem(MUSIC_OWNER_KEY);
    };

    const pauseLocalAudio = () => {
        if (audioRef.current && !audioRef.current.paused) {
            savePlaybackSnapshot(audioRef.current.currentTime);
            audioRef.current.pause();
        }
        setIsPlaying(false);
    };

    const hasBrowserSessionCookie = () => {
        return document.cookie.split('; ').some((cookie) => cookie.startsWith('musicBrowserSession='));
    };

    const setBrowserSessionCookie = () => {
        document.cookie = `${MUSIC_BROWSER_SESSION_COOKIE}; path=/; SameSite=Lax`;
    };

    useEffect(() => {
        if (!hasBrowserSessionCookie()) {
            localStorage.removeItem(MUSIC_TIME_KEY);
            localStorage.removeItem(MUSIC_OWNER_KEY);
            localStorage.removeItem(MUSIC_USER_PREFERENCE);
            setBrowserSessionCookie();
        }
    }, []);

    // 오디오 볼륨 설정
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = 0.3; // 30% 볼륨
        }
    }, []);

    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (!audioRef.current) return;

            // 유저 의도 변경 감지 (다른 탭에서 play/pause 눌림)
            if (event.key === MUSIC_USER_PREFERENCE) {
                const userPreference = event.newValue as 'play' | 'pause' | null;
                if (userPreference === 'pause') {
                    pauseLocalAudio();
                } else if (userPreference === 'play') {
                    // 다른 탭에서 play를 눌러도 현재 탭에서 자동 재생하지 않는다.
                    setIsPlaying(false);
                }
                return;
            }

            // 권한 변경 감지 (탭 전환)
            if (event.key !== MUSIC_OWNER_KEY) return;

            const newOwner = event.newValue;
            const isOwnerNow = newOwner === tabIdRef.current;

            if (!isOwnerNow && audioRef.current) {
                // 다른 탭이 권한을 받음 - 시간만 저장
                savePlaybackSnapshot(audioRef.current.currentTime);
                audioRef.current.pause();
                setIsPlaying(false);
            } else if (isOwnerNow && audioRef.current) {
                // 이 탭이 권한을 받더라도 자동 재생하지 않는다.
                const savedTime = localStorage.getItem(MUSIC_TIME_KEY);
                if (savedTime) {
                    audioRef.current.currentTime = parseFloat(savedTime);
                }
                audioRef.current.pause();
                setIsPlaying(false);
            }

            if (!newOwner) {
                setHasUserInteracted(false);
            }
        };

        const handleBeforeUnload = () => {
            releaseOwner();
        };

        const handleForcePause = () => {
            saveUserPreference('pause');
            pauseLocalAudio();
            releaseOwner();
        };

        window.addEventListener('storage', handleStorage);
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener(MUSIC_FORCE_PAUSE_EVENT, handleForcePause as EventListener);

        return () => {
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener(MUSIC_FORCE_PAUSE_EVENT, handleForcePause as EventListener);
            releaseOwner();
        };
    }, []);

    // 탭 전환 감지 - 탭이 보일 때 권한 획득, 숨겨질 때 권한 포기
    useEffect(() => {
        const handleVisibilityChange = () => {
            const audio = audioRef.current;
            if (!audio) return;

            if (document.hidden) {
                // 탭이 숨겨질 때 - 현재 시간만 저장, user preference는 건드리지 않음
                savePlaybackSnapshot(audio.currentTime);
                if (!audio.paused) {
                    audio.pause();
                }
                releaseOwner();
            } else {
                // 탭이 다시 보일 때 - 유저 의도(preference)를 따름
                const savedTime = localStorage.getItem(MUSIC_TIME_KEY);
                if (savedTime) {
                    audio.currentTime = parseFloat(savedTime);
                }

                // 권한을 획득 (다른 탭에 알림)
                localStorage.setItem(MUSIC_OWNER_KEY, tabIdRef.current);

                // 탭 복귀 시에도 자동 재생하지 않는다.
                audio.pause();
                setIsPlaying(false);
                wasPlayingBeforeHidden.current = false;
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // 재생 중이고 권한이 있으면 1초마다 시간 저장
    useEffect(() => {
        if (!isPlaying || !audioRef.current) return;

        const interval = setInterval(() => {
            if (audioRef.current && isCurrentTabOwner()) {
                savePlaybackSnapshot(audioRef.current.currentTime);
            }
        }, 1000); // 1초마다 저장 (배터리 효율성)

        return () => clearInterval(interval);
    }, [isPlaying]);

    // 탭이 어떤 이유로든 닫힐 때 시간 저장

    useEffect(() => {
        const checkTitleOverflow = () => {
            const viewport = titleViewportRef.current;
            const title = titleTextRef.current;
            if (!viewport || !title) return;

            const viewportWidth = viewport.clientWidth;
            if (viewportWidth <= 0) return;

            setIsTitleOverflowing(title.scrollWidth > viewportWidth);
        };

        const timeoutId = window.setTimeout(checkTitleOverflow, 450);
        window.addEventListener('resize', checkTitleOverflow);

        return () => {
            window.clearTimeout(timeoutId);
            window.removeEventListener('resize', checkTitleOverflow);
        };
    }, [hasUserInteracted]);

    // 재생/일시정지 토글
    const handleTogglePlayback = (e: React.MouseEvent) => {
        e.stopPropagation();
        
        if (audioRef.current) {
            if (isPlaying) {
                // pause 버튼 클릭 - 유저 의도 저장
                saveUserPreference('pause');
                savePlaybackSnapshot(audioRef.current.currentTime);
                audioRef.current.pause();
                setIsPlaying(false);
                releaseOwner();
            } else {
                // play 버튼 클릭 - 유저 의도 저장
                const owned = claimOwner();
                if (!owned) {
                    setHasUserInteracted(false);
                    setIsPlaying(false);
                    return;
                }
                saveUserPreference('play');
                audioRef.current.play().catch(() => {});
                setHasUserInteracted(true);
                setIsPlaying(!audioRef.current.paused);
                savePlaybackSnapshot(audioRef.current.currentTime);
            }
        }
    };

    return (
        <div className="music-player">
            <audio
                ref={audioRef}
                loop
                preload="auto"
            >
                {/* <source src={`${process.env.PUBLIC_URL}/audio/BBOWebBgm.mp3`} type="audio/mpeg" /> */}
                <source src={`${process.env.PUBLIC_URL}/audio/R.Tee_ROCKING_BEAR_(feat._MLMA)_[Extended_Mix].wav`} type="audio/wav" />
            </audio>

            <div className={`music-player-container ${!hasUserInteracted ? 'show-info' : ''}`}>
                {/* 회전하는 원반 */}
                <div className="music-disc-wrapper" onClick={handleTogglePlayback}>
                    <img
                        className={`music-disc spinning ${isPlaying ? 'playing' : 'paused'}`}
                        src={`${process.env.PUBLIC_URL}/images/bgm/bgmDisk.png`}
                        alt="BGM"
                    />
                    {/* 호버시 나타나는 재생/일시정지 버튼 */}
                    <button
                        className="music-control-overlay"
                        onClick={handleTogglePlayback}
                        title={isPlaying ? '음악 멈추기' : '음악 재생'}
                        aria-label={isPlaying ? '음악 멈추기' : '음악 재생'}
                    >
                        <svg
                            className="control-icon"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                        >
                            {isPlaying ? (
                                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                            ) : (
                                <path d="M8 5v14l11-7z" />
                            )}
                        </svg>
                    </button>
                </div>

                {/* 반투명 바 영역 - 제목만 표시 */}
                <div className="music-info-bar" onClick={handleTogglePlayback}>
                    <div
                        ref={titleViewportRef}
                        className={`music-title-viewport ${isTitleOverflowing ? 'marquee' : ''}`}
                    >
                        <div className={`music-title-track ${isTitleOverflowing ? 'marquee' : ''}`}>
                            <span ref={titleTextRef} className="music-title">R.Tee – ROCKING BEAR (feat. MLMA) [Extended Mix]</span>
                            {isTitleOverflowing && (
                                <span className="music-title music-title-duplicate" aria-hidden="true">R.Tee – ROCKING BEAR (feat. MLMA) [Extended Mix]</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MusicPlayer;
